
import React, { useMemo, useState } from 'react';
import { ServiceAssignment, ServiceItem, ServiceTransaction } from '../types';
import { X, Wallet, Plus, ChevronRight, ListChecks } from 'lucide-react';
import { Button } from './Button';
import { ClampedText } from './ClampedText';
import { flatCoverageHints } from '../services/flatRateLogic';
import { contractedQuantity, isNepService, NEP_BADGE, NEP_HINT } from '../services/nepPosition';
import { transferOriginLabel } from '../services/projectLv';
import { blockedAssignmentChoices } from '../services/assignmentChange';
import { ServiceDetailView } from './ServiceDetailView';
import { hatBestandteile } from '../services/bestandteile/bestandteilLogic';
import type { BestandteileKontext } from './bestandteile/BestandteileDialog';
import { plaketteFuer, PLAKETTEN_KLASSE, stundenPlaketteFuer, type OffenUeberschreibung } from './bestandteile/bestandteileDialogLogic';

interface Props {
  services: ServiceItem[];
  /**
   * Leistungsprotokoll des Projekts (beim Tochterprojekt das der Mutter) —
   * Quelle der Detailansicht je Position. Ohne Protokoll zeigt sie nur die
   * Kennzahlen.
   */
  serviceLog?: ReadonlyArray<ServiceTransaction>;
  onClose: () => void;
  onTrack: (service: ServiceItem) => void;
  canSeeFinancials?: boolean;
  /**
   * Nur-Ansicht: der Buchen-Knopf entfaellt ganz.
   *
   * Ohne diese Prop stand er auch im Lesemodus da und der Klick lief in ein
   * `onTrack`, das der Aufrufer verwirft — ein Knopf, der nichts tut, sieht
   * aus wie ein Fehler der Anwendung. Dasselbe Muster wie in ProjectStats
   * (Schnellzugriff, Pauschalmonat, Notiz-Composer): gesperrt wird durch
   * NICHT-Anzeigen, nicht durch einen grauen Knopf.
   */
  isReadOnly?: boolean;
  /** Funktion einer Position umstellen — siehe ServiceDetailView. */
  onChangeAssignment?: (service: ServiceItem, next: ServiceAssignment) => void;
  /**
   * PAKETPOSITION (Position mit Bestandteilen): kein „+" — Pakete bucht nur
   * der Bestandteile-Weg (Kernregel 7). Die Karte traegt die Plakette und
   * „Bestandteile ansehen" (oeffnet den Dialog, ohne Rueckruf die Details).
   */
  onOpenBestandteile?: (service: ServiceItem) => void;
  /** Fuer den Abschnitt „Bestandteile" in der Detailansicht. */
  bestandteileKontext?: BestandteileKontext;
  /** Lokale Ueberschreibung des Stands aus dem Hub (nur Anzeige). */
  bestandteileOffen?: OffenUeberschreibung;
}

const EMPTY_LOG: ReadonlyArray<ServiceTransaction> = [];

export const ServiceDetailsModal: React.FC<Props> = ({ services, serviceLog = EMPTY_LOG, onClose, onTrack, canSeeFinancials = true, isReadOnly = false, onChangeAssignment, onOpenBestandteile, bestandteileKontext, bestandteileOffen }) => {
  const currency = (val: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val);
  // Angeklickte Position — dann zeigt der Dialog statt der Liste ihre Details.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = selectedId ? services.find(s => s.id === selectedId) : undefined;

  // MONATSPAUSCHALE: der ruhige Hinweis je gedeckter Position ("in der
  // Monatspauschale enthalten (…)"). Frisch aus der Liste gerechnet, weil
  // dieser Dialog den rohen globalData-Bestand ohne Deckungsstempel bekommt
  // (siehe flatCoverageHints). Der Leistungsstand daneben laeuft bewusst
  // weiter — gedeckt ist nur das Geld, nicht die Arbeit.
  const flatHints = useMemo(() => flatCoverageHints(services), [services]);

  // Funktionen, die die Deckung einer Monatspauschale verschoeben — nur fuer
  // die geoeffnete Position und nur, wenn umgestellt werden darf.
  const blockedAssignments = useMemo(
      () => (selected && onChangeAssignment ? blockedAssignmentChoices(services, selected.id) : undefined),
      [selected, onChangeAssignment, services],
  );

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
        
        <div className="bg-slate-900 text-white p-6 flex justify-between items-center shrink-0">
            <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                    <Wallet className="w-6 h-6 text-emerald-400" />
                    Leistungen Details
                </h3>
                <p className="text-slate-400 text-sm mt-1">{selected ? 'Einzelne Position mit allen Einträgen' : 'Vollständige Übersicht aller Positionen · Position anklicken für Details'}</p>
            </div>
            <button onClick={onClose} className="hover:bg-white/10 p-2 rounded-full transition-colors"><X className="w-6 h-6"/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
            {selected ? (
                <ServiceDetailView
                    service={selected}
                    serviceLog={serviceLog}
                    flatHint={flatHints.get(selected.id)}
                    canSeeFinancials={canSeeFinancials}
                    isReadOnly={isReadOnly}
                    onBack={() => setSelectedId(null)}
                    onTrack={onTrack}
                    onChangeAssignment={onChangeAssignment}
                    blockedAssignments={blockedAssignments}
                    bestandteile={bestandteileKontext}
                    onOpenBestandteile={onOpenBestandteile}
                    bestandteileOffen={bestandteileOffen}
                />
            ) : (
            <div className="grid gap-4">
                {services.map(service => {
                    // NEP: Soll ist, was gebucht wurde — die Angebotsmenge
                    // steht nur im Hinweis (contractedQuantity).
                    const totalQty = contractedQuantity(service);
                    const usedQty = service.quantityUsed || 0;
                    
                    const totalPrice = totalQty * (service.unitPrice || 0);
                    const usedPrice = usedQty * (service.unitPrice || 0);

                    const percent = canSeeFinancials 
                        ? (totalPrice > 0 ? (usedPrice / totalPrice) * 100 : 0)
                        : (totalQty > 0 ? (usedQty / totalQty) * 100 : 0);

                    const isOver = percent > 100;
                    // Paketposition: Plakette statt „+" (Kernregel 7).
                    const paket = hatBestandteile(service);
                    const plakette = paket ? plaketteFuer(service, bestandteileOffen) : null;
                    // Stunden gegen das Kontingent (intern) — fehlt ohne Kontingent und ohne Stunden.
                    const stundenPlakette = paket ? stundenPlaketteFuer(service, bestandteileOffen) : null;

                    return (
                        <div
                            key={service.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedId(service.id)}
                            onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setSelectedId(service.id); } }}
                            title="Details dieser Position anzeigen"
                            className={`bg-white rounded-xl border p-4 shadow-sm group cursor-pointer transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${isOver ? 'border-red-200 bg-red-50/20 hover:border-red-300' : 'border-slate-200 hover:border-blue-300'}`}
                        >
                            <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
                                <div className="flex-1 w-full">
                                    {/* flex-wrap: mit der Stunden-Plakette wird die Reihe auf dem Handy sonst zu breit. */}
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <span className="font-mono text-xs font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{service.lvPosition}</span>
                                        {service.clientLvPosition && <span className="font-mono text-xs text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">Kunde: {service.clientLvPosition}</span>}
                                        {isNepService(service) && <span className="text-[11px] font-black text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded" title={NEP_HINT}>{NEP_BADGE}</span>}
                                        {plakette && <span className={`text-[11px] font-semibold border px-1.5 py-0.5 rounded ${PLAKETTEN_KLASSE[plakette.ton]}`} title={plakette.titel}>{plakette.text}</span>}
                                        {stundenPlakette && <span className={`text-[11px] font-semibold tabular-nums border px-1.5 py-0.5 rounded ${PLAKETTEN_KLASSE[stundenPlakette.ton]}`} title={stundenPlakette.titel}>{stundenPlakette.text}</span>}
                                    </div>
                                    <h4 className="font-bold text-slate-800 text-base mb-2 flex items-start gap-1 group-hover:text-blue-700 transition-colors"><ClampedText as="span" text={service.title} lines={2} className="min-w-0 break-words" /><ChevronRight className="w-4 h-4 mt-1 shrink-0 text-slate-300 group-hover:text-blue-500" /></h4>
                                    {/* NEP: die Position stand im Angebot nur mit Einheitspreis und
                                        ist nicht beauftragt. Ohne diesen Hinweis sah sie hier aus wie
                                        jede andere, und die Buchung darauf fiel erst bei der Rechnung
                                        auf. Der Buchungsdialog verlangt dafuer eine Begruendung. */}
                                    {isNepService(service) && (
                                        <div className="text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-block mb-2">
                                            {NEP_HINT} · Buchung nur mit Begründung · Angebotsmenge {service.quantityContracted} {service.unit}, zählt erst mit der Buchung
                                        </div>
                                    )}
                                    {/* HERKUNFT: eine Mutter mit mehreren Toechtern fuehrt mehrere
                                        Zeilen derselben LV-Nummer und desselben Titels. Ohne diesen
                                        Hinweis sind sie hier und im Buchungsdialog nicht zu
                                        unterscheiden, und die Buchung landet bei der Schwester. */}
                                    {transferOriginLabel(service) && (
                                        <div className="text-[11px] font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 inline-block mb-2">
                                            {transferOriginLabel(service)}
                                        </div>
                                    )}
                                    {flatHints.has(service.id) && (
                                        <div className="text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5 inline-block mb-2">
                                            {flatHints.get(service.id)}
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-4 text-xs text-slate-600">
                                        <div className="flex items-center gap-1">
                                            <span className="text-slate-400 uppercase font-bold text-[11px]">Einheit:</span> 
                                            {service.unit}
                                        </div>
                                        {canSeeFinancials && (
                                            <div className="flex items-center gap-1">
                                                <span className="text-slate-400 uppercase font-bold text-[11px]">Einzelpreis:</span> 
                                                {currency(service.unitPrice)}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="w-full md:w-auto flex items-center gap-4">
                                    <div className="w-full md:w-64 bg-slate-50 rounded-lg p-3 border border-slate-100 flex flex-col justify-center">
                                        <div className="flex justify-between items-end mb-2">
                                            <div className="text-xs text-slate-500">
                                                {isNepService(service)
                                                    ? <><span className="font-bold">{service.quantityUsed}</span> {service.unit} gebucht · {NEP_BADGE}</>
                                                    : <><span className="font-bold">{service.quantityUsed}</span> / {service.quantityContracted} {canSeeFinancials ? 'verbraucht' : service.unit}</>}
                                            </div>
                                            <div className={`text-sm font-bold ${isOver ? 'text-red-600' : 'text-emerald-600'}`}>
                                                {percent.toFixed(1)}%
                                            </div>
                                        </div>
                                        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                            <div className={`h-full ${isOver ? 'bg-red-500' : 'bg-emerald-500'}`} style={{width: `${Math.min(100, percent)}%`}}></div>
                                        </div>
                                        <div className="mt-2 flex justify-between text-[11px] text-slate-400 font-mono">
                                            <span>Ist: {canSeeFinancials ? currency(usedPrice) : usedQty}</span>
                                            <span>Soll: {canSeeFinancials ? currency(totalPrice) : totalQty}</span>
                                        </div>
                                    </div>

                                    {paket ? (
                                        // Ansehen geht auch in der Nur-Ansicht; ob gebucht
                                        // werden darf, entscheidet der Dialog.
                                        <button
                                            onClick={(e) => { e.stopPropagation(); if (onOpenBestandteile) onOpenBestandteile(service); else setSelectedId(service.id); }}
                                            className="h-12 px-3 rounded-xl bg-white text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1.5 border border-slate-200 transition-colors shrink-0 text-xs font-semibold"
                                            title="Bestandteile ansehen"
                                        >
                                            <ListChecks className="w-4 h-4" /> Bestandteile ansehen
                                        </button>
                                    ) : !isReadOnly && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onTrack(service); }}
                                            className="h-12 w-12 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white flex items-center justify-center border border-blue-100 transition-all shadow-sm shrink-0"
                                            title="Leistung erfassen"
                                        >
                                            <Plus className="w-6 h-6" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {services.length === 0 && (
                    <div className="text-center py-12 text-slate-400 italic">Keine Leistungen vorhanden.</div>
                )}
            </div>
            )}
        </div>

        <div className="p-4 bg-white border-t flex justify-end">
            <Button onClick={onClose} variant="secondary">Schließen</Button>
        </div>
      </div>
    </div>
  );
};
